import React, { useState, useEffect } from 'react';
import { reactionsService, type ReactionType, type ReactionTargetType } from '../../services/reactionsService';

interface EmojiReactionsProps {
  targetType: ReactionTargetType;
  targetId: string;
  currentUserId?: string;
  onReactionChange?: (counts: Record<string, number>, userReaction: ReactionType | null) => void;
  className?: string;
}

// Emoji mappings for reactions
const REACTION_EMOJIS: Record<ReactionType, string> = {
  LIKE: '👍',
  LOVE: '❤️',
  HAHA: '😄',
  WOW: '😮',
  SAD: '😢',
  ANGRY: '😠',
  THUMBS_UP: '👍',
  FIRE: '🔥',
  HEART: '❤️',
};

const REACTION_LABELS: Record<ReactionType, string> = {
  LIKE: 'Like',
  LOVE: 'Love',
  HAHA: 'Haha',
  WOW: 'Wow',
  SAD: 'Sad',
  ANGRY: 'Angry',
  THUMBS_UP: 'Thumbs Up',
  FIRE: 'Fire',
  HEART: 'Heart',
};

const REACTION_TYPES: ReactionType[] = ['LIKE', 'LOVE', 'HAHA', 'WOW', 'SAD', 'FIRE', 'HEART'];

/**
 * Emoji Reactions Component
 * Displays reaction picker and counts for posts/comments
 */
const EmojiReactions: React.FC<EmojiReactionsProps> = ({
  targetType,
  targetId,
  currentUserId,
  onReactionChange,
  className = '',
}) => {
  const [reactionCounts, setReactionCounts] = useState<Record<string, number>>({});
  const [userReaction, setUserReaction] = useState<ReactionType | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadReactions();
  }, [targetId, targetType]);

  const loadReactions = async () => {
    try {
      setLoading(true);
      const res = await reactionsService.getReactions(targetType, targetId);
      setReactionCounts(res.reactionCounts || {});
      setUserReaction(res.userReaction);
      if (onReactionChange) {
        onReactionChange(res.reactionCounts || {}, res.userReaction);
      }
    } catch (error) {
      console.error('Failed to load reactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReaction = async (reactionType: ReactionType) => {
    if (!currentUserId) {
      alert('Please login to react');
      return;
    }

    // If user already has this reaction, remove it
    if (userReaction === reactionType) {
      try {
        setIsSubmitting(true);
        const res = await reactionsService.removeReaction(targetType, targetId);
        setReactionCounts(res.reactionCounts || {});
        setUserReaction(null);
        if (onReactionChange) {
          onReactionChange(res.reactionCounts || {}, null);
        }
      } catch (error: any) {
        console.error('Failed to remove reaction:', error);
        alert(error?.response?.data?.message || 'Failed to remove reaction');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Add or change reaction
      try {
        setIsSubmitting(true);
        const res = await reactionsService.addReaction({
          targetType,
          targetId,
          reactionType,
        });
        setReactionCounts(res.reactionCounts || {});
        setUserReaction(res.userReaction);
        if (onReactionChange) {
          onReactionChange(res.reactionCounts || {}, res.userReaction);
        }
        setShowPicker(false);
      } catch (error: any) {
        console.error('Failed to add reaction:', error);
        alert(error?.response?.data?.message || 'Failed to add reaction');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const getTotalReactions = () => {
    return Object.values(reactionCounts).reduce((sum, count) => sum + count, 0);
  };

  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="text-gray-400 text-sm">Loading reactions...</div>
      </div>
    );
  }

  const totalReactions = getTotalReactions();

  return (
    <div className={`emoji-reactions flex items-center gap-2 ${className}`}>
      {/* Reaction Button */}
      <div className="relative">
        <button
          onClick={() => setShowPicker(!showPicker)}
          disabled={isSubmitting || !currentUserId}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors ${
            userReaction
              ? 'bg-patreon-orange/10 border-patreon-orange text-patreon-orange'
              : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
          } ${isSubmitting || !currentUserId ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {userReaction ? (
            <>
              <span className="text-lg">{REACTION_EMOJIS[userReaction]}</span>
              <span className="text-sm font-medium">{REACTION_LABELS[userReaction]}</span>
            </>
          ) : (
            <>
              <span className="text-lg">👍</span>
              <span className="text-sm font-medium">React</span>
            </>
          )}
        </button>

        {/* Reaction Picker */}
        {showPicker && currentUserId && (
          <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-10 flex gap-1">
            {REACTION_TYPES.map((reactionType) => (
              <button
                key={reactionType}
                onClick={() => handleReaction(reactionType)}
                disabled={isSubmitting}
                className={`p-2 rounded-full hover:bg-gray-100 transition-colors ${
                  userReaction === reactionType ? 'bg-patreon-orange/20' : ''
                }`}
                title={REACTION_LABELS[reactionType]}
              >
                <span className="text-2xl">{REACTION_EMOJIS[reactionType]}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Reaction Counts */}
      {totalReactions > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          {Object.entries(reactionCounts)
            .filter(([_, count]) => count > 0)
            .sort(([a], [b]) => {
              // Sort by count descending, then by type
              const countDiff = reactionCounts[b] - reactionCounts[a];
              if (countDiff !== 0) return countDiff;
              return a.localeCompare(b);
            })
            .map(([reactionType, count]) => (
              <button
                key={reactionType}
                onClick={() => handleReaction(reactionType as ReactionType)}
                disabled={isSubmitting || !currentUserId}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-colors ${
                  userReaction === reactionType
                    ? 'bg-patreon-orange/20 text-patreon-orange'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } ${isSubmitting || !currentUserId ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={`${REACTION_LABELS[reactionType as ReactionType]}: ${count}`}
              >
                <span>{REACTION_EMOJIS[reactionType as ReactionType]}</span>
                <span className="font-medium">{count}</span>
              </button>
            ))}
        </div>
      )}

      {/* Click outside to close picker */}
      {showPicker && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowPicker(false)}
        />
      )}
    </div>
  );
};

export default EmojiReactions;


